import { useEffect, useState, useCallback } from "react";
import API from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Boxes, Loader2, ShieldCheck } from "lucide-react";

export default function ContentGuardAudit() {
  const [data, setData] = useState({ sessions: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      // Pull a list of recent sessions via management endpoint; if not present
      // we fall back to the request directory and surface ContentGuard sessions
      // per job via the existing contents API.
      const sessions = [];
      const reqs = await API.get("/management/requests?limit=50");
      for (const r of reqs.data.requests || []) {
        try {
          const s = await API.get(`/contents/session/${r.id}`);
          if (s.data.session) {
            sessions.push({ request: r, session: s.data.session, areas: s.data.areas || [],
                            items_count: s.data.items_count, boxes_count: s.data.boxes_count });
          }
        } catch (e) { /* skip */ }
      }
      setData({ sessions });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-6 h-6 animate-spin text-zinc-500" />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="contentguard-audit-page">
      <div>
        <h1 className="text-2xl font-heading font-bold text-zinc-900 flex items-center gap-2">
          <Boxes className="w-6 h-6 text-red-600" /> ContentGuard™ Audit
        </h1>
        <p className="text-sm text-zinc-500">Cross-job overview of all active contents inventory sessions.</p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {data.sessions.map(({ request, session, areas, items_count, boxes_count }) => (
          <Card key={session.id} className="border border-zinc-200" data-testid={`cg-session-${session.id}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-mono text-zinc-400">{request.request_code}</p>
                <ShieldCheck className="w-4 h-4 text-red-600" />
              </div>
              <p className="text-sm font-semibold text-zinc-900">{request.service_type_label}</p>
              <p className="text-[11px] text-zinc-500 mb-3">{session.tech_name}</p>
              <div className="grid grid-cols-3 text-center gap-2">
                <div><p className="text-[10px] text-zinc-400">AREAS</p><p className="font-bold">{areas.length}</p></div>
                <div><p className="text-[10px] text-zinc-400">ITEMS</p><p className="font-bold">{items_count}</p></div>
                <div><p className="text-[10px] text-zinc-400">BOXES</p><p className="font-bold">{boxes_count}</p></div>
              </div>
              <p className="text-[10px] text-zinc-500 mt-2">Opened {new Date(session.opened_at).toLocaleString()}</p>
            </CardContent>
          </Card>
        ))}
        {data.sessions.length === 0 && (
          <p className="col-span-full text-xs text-zinc-400 italic text-center py-12">No active ContentGuard sessions.</p>
        )}
      </div>
    </div>
  );
}
